/* ================================================================
   ZZ MARTS - CART: TRUST BLOCK, CONTINUE SHOPPING, QTY, DELIVERY BAR
   ----------------------------------------------------------------
     1. Four reassurances plus a WhatsApp line - side cart AND /cart
     2. A "Continue shopping" link under the checkout button - drawer only
     3. The quantity control fix - see TRAP 24
     4. The free delivery progress bar - /cart ONLY (the drawer's own
        copy is drawn by zz-cart.js)

   WHY JAVASCRIPT AND NOT LIQUID: the right Liquid home is
   snippets/cart-summary.liquid, which renders the checkout button on
   both surfaces. That file is 18KB of core Horizon, and a single slip
   in it breaks checkout - the one page where a mistake costs an order
   rather than a click. Injecting means a failure loses a reassurance,
   not a function. That is the correct way round.

   WHY THE CSS IS IN HERE RATHER THAN A NEW STYLESHEET: a new asset
   only loads if something registers it in snippets/zz-head.liquid,
   which renders in the <head> of every page. Editing a 14KB head
   snippet to register one small file puts the whole site's CSS, JS
   and structured data at risk to fix one drawer. This file is already
   loaded and its blast radius is the cart.

   ================================================================
   >>> TWO COPY LENGTHS, AND WHY THAT IS NOT A CONTRADICTION <<<
   ================================================================
   This file used to carry a note saying "do not shorten these into
   generic badges", because two of the lines answer a real objection:
   "you pay the courier, not us" addresses who holds the money, which
   is the actual fear behind cash on delivery here.

   That note was right about the /cart page and wrong about the
   drawer. In the drawer the block ran to SEVEN wrapped lines and,
   stacked with the delivery bar, the discount row and the totals, the
   footer took 58% of the panel. The customer could see about one and
   a half of their own items. A reassurance that pushes the basket off
   screen is not reassuring.

   So there are now two lengths of the same four promises:

     FULL     used on /cart, where there is room to breathe
     COMPACT  used inside #cart-drawer, tuned so every line fits on
              ONE line at 432px, the drawer width set in zz-cart.css

   The compact copy keeps the substance - courier not us, the real
   threshold, the cities, the 7 days. It drops only padding words.
   Measured after the change: all four rows are exactly one line high.

   ================================================================
   >>> THE SAME PROMISE WAS BEING MADE TWICE <<<
   ================================================================
   Once the cart qualifies, the progress bar said "Free delivery
   applied to this order." and the trust list said "Free delivery on
   this order" about 120px below it. Two statements of one fact, plus
   a progress bar tracking a goal already reached, plus the hairline
   between them - 37px of a footer already too tall.

   So in the drawer the bar hides itself once the threshold is met and
   the trust line takes over as the confirmation, in brand gold so it
   still reads as a win. Below the threshold the bar is doing real
   work - it shows how far off you are - so it stays. The /cart page
   keeps both; there is room there.

   Paired with the is-free class set in buildTrust. They are ONE
   decision. Delete one and you must delete the other, or the customer
   loses the free delivery confirmation entirely.

   ================================================================
   >>> TRAP 24 - .cart-items__quantity-controls IS NOT THE CONTROL <<<
   ================================================================
   The class name says it is the quantity control. It is not. Horizon
   nests TWO separate things inside it:

     .cart-items__quantity-controls        <- wrapper
       .quantity-selector-wrapper
         cart-quantity-selector-component  <- the actual control
           button.quantity-minus           (icon 14px)
           input
           button.quantity-plus            (icon 14px)
       button.cart-items__remove           (icon 20px)  <- ALSO INSIDE

   zz-cart.css put the rounded pill - border, radius, white fill,
   padding - on the WRAPPER, and sized every descendant button to
   26x26. Two faults at once:

     1. The Remove (bin) button was swallowed into the pill, so a
        destructive action sat inside the same bordered capsule as
        plus and minus and read as a third quantity button.
     2. Horizon ships the bin icon at 20px while plus and minus are
        14px. Forced into the same 26px box the bin had 3px of
        clearance against their 6px, so it looked jammed and visibly
        larger than its neighbours.

   Together, that is what "the quantity button looks broken" was.

   The fix puts the pill where the control actually is: the wrapper
   becomes a plain flex row, .quantity-selector gets the pill, and the
   bin sits outside it with its own hover state. Icon sizes are
   normalised so nothing depends on Horizon shipping matched icons.

   Buttons went back to 28px. 26px was chosen to save footer height
   and it shrank a control people have to hit accurately on a phone.
   The saving was two pixels.

   If you restyle this, check what is INSIDE the element you are
   styling first. The class name lied.

   ================================================================
   >>> CONTINUE SHOPPING CLOSES THE DRAWER, IT DOES NOT NAVIGATE <<<
   ================================================================
   The obvious implementation is a link to /collections/all. That is
   wrong here: the customer is already on a page, usually a product or
   collection page they were part way through. Navigating them away
   from it to a generic list loses their place and their scroll
   position, which is the opposite of "continue shopping".

   So the button CLOSES THE DRAWER and leaves them exactly where they
   were. It does that by clicking Horizon's own close button rather
   than calling any component method, because the close button is
   plain markup that is unlikely to change, while the component API is
   internal and has already caught us out once (see TRAP 10 in
   zz-cart.css). If the close button is ever missing, the handler does
   nothing rather than throwing.

   It is a quiet text link, not a second button. Two buttons of equal
   weight next to each other make the customer choose; the primary
   action must stay obviously primary.

   >>> THE PHONE NUMBER IS GONE ON PURPOSE <<<
   The line used to print 0308 3260009 as link text. In a 432px
   drawer it wrapped mid-number, so the customer saw "0308" on one
   line and "3260009" on the next, which reads as a broken layout and,
   worse, as a number you cannot trust to be complete. A text link
   cannot wrap into something that looks like a different number.
   Do not put the digits back as link text.

   THE FREE DELIVERY LINE IS CONDITIONAL. It reads "Free delivery on
   this order" only once the cart actually qualifies; below the
   threshold it states the threshold instead. Telling someone with
   Rs 900 in their basket that delivery is free is a promise checkout
   then breaks.

   THE THRESHOLD IS READ, NOT DECLARED. window.ZZ_FREE_SHIP is
   published by zz-cart.js, which loads first. A drawer and a cart
   page promising two different amounts is exactly the class of bug
   this theme keeps producing. The fallback below exists only for the
   case where zz-cart.js fails to load at all.

   WHY A MutationObserver: Horizon re-renders the cart summary
   wholesale on every change. Anything injected once on load is gone
   by the second interaction. Debounced with requestAnimationFrame
   because one cart update fires many mutations, and the data-
   attribute guards make insertion idempotent - without them the
   observer reacts to its own work and loops.
   ================================================================ */
(function () {
  'use strict';

  var MARK = 'data-zz-cart-trust';
  var SHIP = 'data-zz-ship';
  var CONT = 'data-zz-continue';
  var WA = '923083260009';

  var ICONS = {
    wallet: '<path d="M3 7h14a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2z"/><path d="M3 7V6a2 2 0 012-2h9"/><path d="M16 12h.01"/>',
    truck:  '<path d="M3 6h10v9H3z"/><path d="M13 9h4l3 3v3h-7z"/><circle cx="7" cy="18" r="1.6"/><circle cx="16" cy="18" r="1.6"/>',
    clock:  '<circle cx="12" cy="12" r="8.5"/><path d="M12 7.5V12l3 1.8"/>',
    back:   '<path d="M3 10h11a5 5 0 010 10h-4"/><path d="M6.5 6.5L3 10l3.5 3.5"/>'
  };

  function threshold() {
    return (typeof window.ZZ_FREE_SHIP === 'number' && window.ZZ_FREE_SHIP > 0)
      ? window.ZZ_FREE_SHIP
      : 500000;
  }

  function rupees(paise) {
    return 'Rs ' + Math.ceil(paise / 100).toLocaleString('en-US');
  }

  /* ---------------- styles, injected once ----------------
     This <style> is appended to <head> at runtime, so it comes after
     every stylesheet_tag in document order and wins ties on load
     order. That is what lets it correct zz-cart.css without needing
     higher specificity or !important. */

  function injectStyles() {
    if (document.getElementById('zz-cart-trust-css')) return;
    var css = [
      /* ---- /cart page: the roomy version ---- */
      '.zzct{margin:0 0 14px;padding:14px 16px;border:1px solid var(--zz-border,#E9E6DE);',
      'border-radius:14px;background:var(--zz-muted-2,#FBFAF7);display:grid;gap:9px;width:100%;',
      'box-sizing:border-box;list-style:none}',
      '.zzct li{display:flex;align-items:flex-start;gap:10px;margin:0;font-size:0.8125rem;line-height:1.4;',
      'font-weight:550;color:var(--zz-ink,#17150F)}',
      '.zzct svg{width:17px;height:17px;flex:0 0 17px;margin-top:1px;color:var(--zz-accent-deep,#8A6A00)}',
      '.zzct li.is-free{color:var(--zz-accent-deep,#8A6A00);font-weight:650}',
      '.zzct__ask{margin:2px 0 0;padding-top:10px;border-top:1px solid var(--zz-border,#E9E6DE);',
      'font-size:0.78125rem;line-height:1.45;color:var(--zz-muted-fg,#6B6862);font-weight:450}',
      '.zzct__ask a{color:var(--zz-ink,#17150F);font-weight:650;text-decoration:underline;text-underline-offset:2px}',

      /* ---- drawer: the compact version ----
         No card, no border, no fill, and NO rule above the WhatsApp
         line. In a panel this narrow a bordered box inside a tinted
         footer reads as clutter, and the hairlines were exactly what
         the owner was seeing too many of. */
      '#cart-drawer .zzct{border:0;background:none;padding:0 0 10px;margin:0;gap:6px}',
      '#cart-drawer .zzct li{font-size:0.75rem;line-height:1.35;align-items:center;gap:9px;font-weight:500}',
      '#cart-drawer .zzct svg{width:15px;height:15px;flex:0 0 15px;margin-top:0}',
      '#cart-drawer .zzct__ask{border-top:0;padding-top:0;margin-top:5px;font-size:0.75rem;line-height:1.35}',

      /* See "THE SAME PROMISE WAS BEING MADE TWICE" in the header. */
      '#cart-drawer .zzcart__ship.is-unlocked{display:none}',

      /* ---- quantity control, see TRAP 24 ----
         The pill moves off the WRAPPER and onto the actual selector,
         so the Remove bin stops being trapped inside it. */
      '#cart-drawer .cart-items__quantity-controls{display:flex;align-items:center;gap:8px;',
      'padding:0;border:0;border-radius:0;background:none}',
      '#cart-drawer .cart-items__quantity-controls .quantity-selector-wrapper{display:inline-flex}',
      '#cart-drawer .cart-items__quantity-controls .quantity-selector{display:inline-flex;align-items:center;',
      'gap:2px;padding:2px;border:1px solid var(--zz-border,#E9E6DE);border-radius:999px;background:#fff}',
      '#cart-drawer .cart-items__quantity-controls button{width:28px;height:28px;min-width:28px;padding:0;',
      'border-radius:999px;display:grid;place-items:center;color:var(--zz-muted-fg,#6B6862);',
      'transition:background .16s ease,color .16s ease}',
      /* Normalised so the bin can never out-size plus and minus again,
         whatever icon sizes Horizon ships. */
      '#cart-drawer .cart-items__quantity-controls svg{width:14px;height:14px}',
      '#cart-drawer .cart-items__quantity-controls button:hover:not(:disabled){',
      'background:var(--zz-muted,#F5F3EE);color:var(--zz-ink,#17150F)}',
      '#cart-drawer .cart-items__quantity-controls button:disabled{opacity:.4;cursor:default}',
      '#cart-drawer .cart-items__quantity-controls input{width:30px;border:0;text-align:center;',
      'font-size:0.8125rem;font-weight:600;font-variant-numeric:tabular-nums;background:transparent;',
      'color:var(--zz-ink,#17150F)}',
      /* Red only on hover. A permanently red bin on every row makes a
         cart look like a list of mistakes. */
      '#cart-drawer .cart-items__remove{border:0;background:none;color:var(--zz-subtle-fg,#7A766D)}',
      '#cart-drawer .cart-items__remove:hover{background:#FDECEC;color:#C02626}',
      '#cart-drawer .cart-items__remove:focus-visible{outline:2px solid var(--zz-accent,#FFC400);outline-offset:2px}',

      /* ---- continue shopping ----
         A quiet text link, deliberately not a second button. */
      '.zzcont{display:block;width:100%;margin:9px 0 0;padding:4px 0;background:none;border:0;',
      'font:inherit;font-size:0.8125rem;font-weight:550;line-height:1.4;text-align:center;',
      'color:var(--zz-muted-fg,#6B6862);cursor:pointer;text-decoration:underline;',
      'text-underline-offset:2px;text-decoration-color:rgba(23,21,15,.25);',
      'transition:color .16s ease,text-decoration-color .16s ease}',
      '.zzcont:hover{color:var(--zz-ink,#17150F);text-decoration-color:currentColor}',
      '.zzcont:focus-visible{outline:2px solid var(--zz-accent,#FFC400);outline-offset:3px;border-radius:6px}',

      /* Below about 400px the compact lines start to wrap anyway, so the
         type steps down rather than the layout breaking. */
      '@media (max-width:399px){#cart-drawer .zzct li{font-size:0.71875rem}}',
      '@media (max-width:479px){.zzct li{font-size:0.78125rem}}',

      /* ---- /cart page free delivery bar ---- */
      '.zzshipbar{margin:0 0 14px;width:100%;box-sizing:border-box}',
      '.zzshipbar__note{margin:0 0 8px;font-size:0.8125rem;font-weight:600;color:var(--zz-ink,#17150F)}',
      '.zzshipbar__track{height:6px;border-radius:999px;background:var(--zz-border,#E9E6DE);overflow:hidden}',
      '.zzshipbar__fill{display:block;height:100%;border-radius:999px;background:var(--zz-accent,#FFC400);',
      'transform-origin:left center;transform:scaleX(0);transition:transform .35s cubic-bezier(.16,1,.3,1)}',
      '.zzshipbar.is-unlocked .zzshipbar__fill{background:#1a9c5b}',
      '.zzshipbar.is-unlocked .zzshipbar__note{color:#147946}',
      '@media (prefers-reduced-motion:reduce){.zzshipbar__fill{transition:none}}'
    ].join('');
    var el = document.createElement('style');
    el.id = 'zz-cart-trust-css';
    el.textContent = css;
    document.head.appendChild(el);
  }

  /* ---------------- trust block ----------------
     Line 2 changes with the cart total, so the block is rebuilt on every
     sync rather than inserted once. `compact` picks the drawer copy.
     Each entry is [icon, text, className]. */

  function buildTrust(cart, compact) {
    var t = threshold();
    var total = (cart && cart.total_price) || 0;
    var qualifies = total >= t;
    var freeCls = qualifies ? 'is-free' : '';

    var items = compact
      ? [
          ['wallet', 'Cash on delivery — pay the courier, not us', ''],
          ['truck',  qualifies ? 'Free delivery on this order'
                               : 'Free delivery over ' + rupees(t), freeCls],
          ['clock',  '2–3 days to Karachi, Lahore, Islamabad', ''],
          ['back',   '7 days to return it if it is not right', '']
        ]
      : [
          ['wallet', 'Cash on delivery — you pay the courier, not us', ''],
          ['truck',  qualifies ? 'Free delivery on this order'
                               : 'Free delivery on orders over ' + rupees(t), freeCls],
          ['clock',  'Delivered in 2–3 working days to Karachi, Lahore and Islamabad', ''],
          ['back',   '7 days to return it if it is not right', '']
        ];

    var ul = document.createElement('ul');
    ul.className = 'zzct';
    ul.setAttribute(MARK, '');

    var html = '';
    for (var i = 0; i < items.length; i++) {
      html += '<li' + (items[i][2] ? ' class="' + items[i][2] + '"' : '') + '>' +
              '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"' +
              ' stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
              ICONS[items[i][0]] + '</svg><span>' + items[i][1] + '</span></li>';
    }

    html += '<li class="zzct__ask">Questions? ' +
            '<a href="https://wa.me/' + WA + '" target="_blank" rel="noopener">Message us on WhatsApp</a>' +
            (compact ? ' — we reply in minutes.' : ' — we usually reply within minutes.') +
            '</li>';

    ul.innerHTML = html;
    return ul;
  }

  /* ---------------- insertion ----------------
     Horizon renders the checkout control differently on the two surfaces, so
     several selectors are tried rather than assuming one. The block goes above
     the BUTTON, not above the totals: the point is to answer "is this safe to
     press" at the moment the customer is deciding to press it. */

  var SELECTORS = [
    'button[name="checkout"]',
    'input[name="checkout"]',
    '[data-testid="Checkout-button"]',
    '.cart__checkout-button',
    '.cart-summary__checkout-button'
  ];

  function findButtons() {
    var found = [];
    for (var i = 0; i < SELECTORS.length; i++) {
      var nodes = document.querySelectorAll(SELECTORS[i]);
      for (var j = 0; j < nodes.length; j++) {
        if (found.indexOf(nodes[j]) === -1) found.push(nodes[j]);
      }
    }
    return found;
  }

  function anchorFor(btn) {
    /* Accelerated wallet buttons sit in their own container next to the real
       checkout button. Anchor to the outermost so blocks land above the whole
       group rather than wedged between two payment buttons. */
    return btn.closest('.cart__checkout-buttons, .cart-summary__buttons, .accelerated-checkout-block') || btn;
  }

  function applyTrust(cart) {
    var buttons = findButtons();
    for (var i = 0; i < buttons.length; i++) {
      var anchor = anchorFor(buttons[i]);
      var parent = anchor.parentElement;
      if (!parent) continue;
      var compact = !!anchor.closest('#cart-drawer');
      var existing = parent.querySelector(':scope > [' + MARK + ']');
      var fresh = buildTrust(cart, compact);
      if (existing) {
        if (existing.innerHTML !== fresh.innerHTML) existing.innerHTML = fresh.innerHTML;
      } else {
        parent.insertBefore(fresh, anchor);
      }
    }
  }

  /* ---------------- continue shopping, drawer only ----------------
     On /cart the customer is already on a full page with the whole site
     around them, so a "continue shopping" control there is noise. In the
     drawer it is the missing exit that is not the small X in the corner.
     See the header for why it closes rather than navigates. */

  function closeDrawer() {
    var btn = document.querySelector('#cart-drawer .theme-drawer__close-button');
    if (btn) btn.click();
  }

  function applyContinue() {
    var buttons = findButtons();
    for (var i = 0; i < buttons.length; i++) {
      var anchor = anchorFor(buttons[i]);
      if (!anchor.closest('#cart-drawer')) continue;
      var parent = anchor.parentElement;
      if (!parent) continue;
      if (parent.querySelector(':scope > [' + CONT + ']')) continue;

      var b = document.createElement('button');
      b.type = 'button';
      b.className = 'zzcont';
      b.setAttribute(CONT, '');
      b.textContent = 'Continue shopping';
      b.addEventListener('click', closeDrawer);
      /* after the checkout button, never before it */
      if (anchor.nextSibling) parent.insertBefore(b, anchor.nextSibling);
      else parent.appendChild(b);
    }
  }

  /* ---------------- free delivery bar, /cart page only ----------------
     The drawer already has one drawn by zz-cart.js. Injecting a second into
     #cart-drawer would show two identical bars, so drawer buttons are skipped. */

  function applyShipBar(cart) {
    if (!cart) return;
    var buttons = findButtons();
    for (var i = 0; i < buttons.length; i++) {
      var btn = buttons[i];
      if (btn.closest('#cart-drawer')) continue;
      var anchor = anchorFor(btn);
      var parent = anchor.parentElement;
      if (!parent) continue;

      var bar = parent.querySelector(':scope > [' + SHIP + ']');
      if (!cart.item_count) { if (bar) bar.remove(); continue; }

      if (!bar) {
        bar = document.createElement('div');
        bar.className = 'zzshipbar';
        bar.setAttribute(SHIP, '');
        bar.innerHTML = '<p class="zzshipbar__note"></p>' +
                        '<div class="zzshipbar__track"><span class="zzshipbar__fill"></span></div>';
        var trust = parent.querySelector(':scope > [' + MARK + ']');
        parent.insertBefore(bar, trust || anchor);
      }

      var t = threshold();
      var total = cart.total_price || 0;
      var unlocked = total >= t;
      bar.classList.toggle('is-unlocked', unlocked);
      bar.querySelector('.zzshipbar__note').textContent = unlocked
        ? 'Free delivery applied to this order.'
        : 'Add ' + rupees(t - total) + ' more for free delivery.';
      bar.querySelector('.zzshipbar__fill').style.transform =
        'scaleX(' + Math.min(1, total / t).toFixed(4) + ')';
    }
  }

  /* ---------------- lifecycle ---------------- */

  var syncing = false;
  function sync() {
    if (syncing || !findButtons().length) return;
    syncing = true;
    fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (cart) { applyTrust(cart); applyContinue(); applyShipBar(cart); })
      .catch(function () {})
      .then(function () { syncing = false; });
  }

  var queued = false;
  function schedule() {
    if (queued) return;
    queued = true;
    requestAnimationFrame(function () { queued = false; sync(); });
  }

  function start() {
    injectStyles();
    sync();
    new MutationObserver(schedule).observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
